import type { ImageLoaderProps } from "next/image";
import { getRuntimeEnv } from "@/lib/env";

function normalizeSrc(src: string) {
  return src.startsWith("/") ? src.slice(1) : src;
}

function encodeForCloudflarePath(value: string) {
  return value
    .replace(/\?/g, "%3F")
    .replace(/&/g, "%26")
    .replace(/=/g, "%3D")
    .replace(/#/g, "%23");
}

function isR2Image(src: string) {
  const env = getRuntimeEnv();
  const bucketName = env.R2_IMAGE_BUCKET_NAME || 'directus-staging';
  // Check if the image is from the R2 bucket
  const isImageId = !src.startsWith('http') && !src.includes('/') && src.includes('.');
  return src.includes(bucketName) || src.startsWith(`/${bucketName}/`) || isImageId;
}

export default function cloudflareLoader({ src, width, quality }: ImageLoaderProps) {
  const env = getRuntimeEnv();
  const defaultQuality = 70;
  const finalQuality = quality || defaultQuality;
  // Default to AVIF; common alternatives: webp, auto, jpeg, png
  const defaultFormat = "avif";
  let formatToUse: string = defaultFormat;

  const params = [
    `width=${width}`,
    `quality=${finalQuality}`,
  ];

  // Normalize and parse optional query string for overrides (e.g., format)
  const rawNormalizedSrc = normalizeSrc(src);
  let normalizedSrc = rawNormalizedSrc;
  let preservedQuery = "";
  let querySegment = "";

  const queryIndex = rawNormalizedSrc.indexOf("?");
  if (queryIndex !== -1) {
    querySegment = rawNormalizedSrc.slice(queryIndex);
    normalizedSrc = rawNormalizedSrc.slice(0, queryIndex);
  } else {
    const encodedQueryIndex = rawNormalizedSrc.toLowerCase().indexOf("%3f");
    if (encodedQueryIndex !== -1) {
      querySegment = decodeURIComponent(rawNormalizedSrc.slice(encodedQueryIndex));
      normalizedSrc = rawNormalizedSrc.slice(0, encodedQueryIndex);
    }
  }

  if (querySegment) {
    const searchParams = new URLSearchParams(
      querySegment.startsWith("?") ? querySegment.slice(1) : querySegment
    );
    const formatOverride = searchParams.get("format") || searchParams.get("fm");
    if (formatOverride) {
      formatToUse = formatOverride;
      searchParams.delete("format");
      searchParams.delete("fm");
    }
    const remainingQuery = searchParams.toString();
    if (remainingQuery) {
      preservedQuery = remainingQuery;
    }
  }
  // Always include explicit format (default or override)
  params.push(`format=${formatToUse}`);
  const paramsString = params.join(",");
  // Handle R2 bucket images
  if (isR2Image(normalizedSrc)) {
    const bucketName = env.R2_IMAGE_BUCKET_NAME || 'directus-staging';
    let imageUrl = normalizedSrc;

    // Handle different src formats
    if (normalizedSrc.includes(bucketName)) {
      // Remove bucket prefix if present (existing full paths)
      imageUrl = normalizedSrc.replace(new RegExp(`^${bucketName}\/`), '');
    } else {
      // Keep as-is for simple image IDs or other paths
      imageUrl = normalizedSrc;
    }

    const imagePath = preservedQuery ? `${imageUrl}?${preservedQuery}` : imageUrl;

    // Use images.pandahub.com for all environments
    return `https://images.pandahub.com/cdn-cgi/image/${paramsString}/${encodeForCloudflarePath(imagePath)}`;
  }

  const normalizedWithQuery = preservedQuery ? `${normalizedSrc}?${preservedQuery}` : normalizedSrc;
  const isAbsoluteUrl = /^https?:\/\//.test(normalizedSrc);

  if (isAbsoluteUrl) {
    const encodedRemotePath = encodeForCloudflarePath(normalizedSrc);
    const querySuffix = preservedQuery ? `?${preservedQuery}` : "";
    return `https://images.pandahub.com/cdn-cgi/image/${paramsString}/${encodedRemotePath}${querySuffix}`;
  }

  // For relative assets fall back to original during local development to avoid 404s
  if (process.env.NODE_ENV === "development") {
    return src;
  }

  // Use images.pandahub.com for all environments
  return `https://images.pandahub.com/cdn-cgi/image/${paramsString}/${encodeForCloudflarePath(normalizedWithQuery)}`;
}
